import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Observes every `.btr-reveal` element in the document and toggles
 * `is-visible` when it enters the viewport.
 *
 * IMPORTANT: This hook re-attaches the observer on every route change
 * because new pages mount fresh DOM nodes that the initial observer
 * was not watching — otherwise those elements stay at opacity 0 and
 * the page appears blank or "text loads late".
 */
export default function useReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Allow paint to commit the new DOM, then attach observer.
    const raf = requestAnimationFrame(() => {
      const els = document.querySelectorAll(".btr-reveal:not(.is-visible)");

      if (!("IntersectionObserver" in window) || els.length === 0) {
        els.forEach((el) => el.classList.add("is-visible"));
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
      );

      // Eagerly reveal any element already within the viewport so the
      // user does not see a blank/late paint on back-navigation.
      const vh = window.innerHeight;
      els.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < vh * 0.95) {
          el.classList.add("is-visible");
        } else {
          io.observe(el);
        }
      });

      // Store on window for cleanup
      window.__btrReveal = io;
    });

    return () => {
      cancelAnimationFrame(raf);
      if (window.__btrReveal) {
        window.__btrReveal.disconnect();
        window.__btrReveal = null;
      }
    };
  }, [pathname]);
}
